// FR-P14 (SD64): which picked files the upload page will send, and as what. Pure — takes a
// name and a browser-reported type, returns strings — so it is testable without a DOM.
//
// The server's `validate_content_type` (server/src/uploads.rs) is the allowlist; this mirrors it
// so an unsupported file is refused before an upload session exists, with the same wording.
// Neither side decides what the bytes really are: the `publish` job's ffprobe does, and a file
// that isn't media fails its publish with a plain-language reason.

/** Extension → the canonical MIME type the server accepts. Used when the browser reports no
 * type (common for .mkv, .m4a and .flac on some platforms) or one the server doesn't list. */
const BY_EXTENSION: Record<string, string> = {
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  mkv: "video/x-matroska",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  wav: "audio/wav",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/ogg",
  flac: "audio/flac",
};

/** Every MIME type `validate_content_type` accepts. */
const ACCEPTED_TYPES = new Set([
  "video/webm",
  "audio/webm",
  "video/mp4",
  "video/quicktime",
  "video/x-matroska",
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/x-m4a",
  "audio/m4a",
  "audio/aac",
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/vnd.wave",
  "audio/ogg",
  "audio/flac",
  "audio/x-flac",
]);

/** For the file input's `accept` attribute: types and extensions, so the OS picker shows
 * files whose type it doesn't know too. */
export const UPLOAD_ACCEPT = [...ACCEPTED_TYPES, ...Object.keys(BY_EXTENSION).map((e) => `.${e}`)].join(",");

export const UNSUPPORTED_FILE_MESSAGE =
  "This file type isn't supported. Upload an MP4, WebM, MOV or MKV video, or an MP3, M4A, AAC, WAV, OGG or FLAC audio file.";

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/** The content type to create the upload with, or null when the file isn't one Grackle takes.
 * The browser's own type wins when the server accepts it; otherwise the extension decides. */
export function uploadContentType(file: { name: string; type: string }): string | null {
  const reported = file.type.split(";")[0].trim().toLowerCase();
  if (ACCEPTED_TYPES.has(reported)) return reported;
  return BY_EXTENSION[extensionOf(file.name)] ?? null;
}

export function isAudioContentType(contentType: string): boolean {
  return contentType.startsWith("audio/");
}

/** A starting title from the file name: extension dropped, separators turned into spaces, and
 * clipped to the server's 200-character limit (§2.10). Never empty. */
export function titleFromFileName(name: string): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const title = stem.replace(/[_]+/g, " ").replace(/\s+/g, " ").trim();
  return (title || "Uploaded file").slice(0, 200);
}
